import type {
  PaginationState,
  SortingState,
  VisibilityState,
} from "@tanstack/react-table";

/** Shape stored under each `tanstackTableNames.*` key (matches DataTable). */
export type TanstackTableLocalPrefs = {
  columnOrder?: string[];
  columnVisibility?: VisibilityState;
  sorting?: SortingState;
  /** Only `pageSize` is persisted (not current page index). */
  pagination?: Pick<PaginationState, "pageSize">;
};

export function readTanstackTablePrefs(
  storageKey: string,
): TanstackTableLocalPrefs {
  if (typeof window === "undefined") return {};
  try {
    const raw = window.localStorage.getItem(storageKey);
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    if (
      parsed !== null &&
      typeof parsed === "object" &&
      !Array.isArray(parsed)
    ) {
      return parsed as TanstackTableLocalPrefs;
    }
    return {};
  } catch {
    return {};
  }
}

export function persistTanstackTablePrefs(
  storageKey: string,
  patch: Partial<TanstackTableLocalPrefs>,
) {
  if (typeof window === "undefined") return;
  try {
    const prev = readTanstackTablePrefs(storageKey);
    const next: TanstackTableLocalPrefs = { ...prev };
    if (patch.columnOrder !== undefined) next.columnOrder = patch.columnOrder;
    if (patch.columnVisibility !== undefined) {
      next.columnVisibility = patch.columnVisibility;
    }
    if (patch.sorting !== undefined) next.sorting = patch.sorting;
    if (patch.pagination !== undefined) {
      next.pagination = {
        ...prev.pagination,
        ...patch.pagination,
      };
    }
    window.localStorage.setItem(storageKey, JSON.stringify(next));
  } catch {
    /* private mode / quota */
  }
}

/** Preserve sorting, visibility and order when a table deliberately renames columns. */
export function migrateTableColumnPrefs<
  T extends Pick<
    TanstackTableLocalPrefs,
    "columnOrder" | "columnVisibility" | "sorting"
  >,
>(
  prefs: T,
  aliases: Record<string, string>,
  newColumnAnchors: Record<string, string> = {},
): T {
  const columnId = (id: string) => aliases[id] ?? id;
  const visibility = Object.entries(prefs.columnVisibility ?? {});
  const columnOrder = prefs.columnOrder
    ? Array.from(new Set(prefs.columnOrder.map(columnId)))
    : undefined;
  if (columnOrder) {
    for (const [newColumnId, anchorId] of Object.entries(newColumnAnchors)) {
      const anchorIndex = columnOrder.indexOf(anchorId);
      if (!columnOrder.includes(newColumnId) && anchorIndex !== -1) {
        columnOrder.splice(anchorIndex + 1, 0, newColumnId);
      }
    }
  }
  return {
    ...prefs,
    ...(columnOrder ? { columnOrder } : {}),
    ...(prefs.sorting
      ? {
          sorting: prefs.sorting.map((sort) => ({
            ...sort,
            id: columnId(sort.id),
          })),
        }
      : {}),
    ...(prefs.columnVisibility
      ? {
          columnVisibility: Object.fromEntries(
            [
              ...visibility.filter(([id]) => id in aliases),
              ...visibility.filter(([id]) => !(id in aliases)),
            ].map(([id, visible]) => [columnId(id), visible]),
          ),
        }
      : {}),
  };
}
